import React from "react";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import { FieldShell, fieldBaseProps } from "./Fields";

/**
 * 搜尋型選擇（design system primitive，模板 Forms > Advanced 的 react-select）。
 * 關閉態對齊 `.form-select`（圓角＋聚焦藍框），多選 tag 對齊 Choices 主色 pill。
 * 樣式在 `assets/scss/custom/plugins/_select2.scss`。
 *
 * 長清單欄位用這個,不要用 <SelectField>:機房、機櫃、管理者、廠商、關聯設備
 * 動輒上百筆,原生 <select> 無法打字過濾。
 *
 *   <SearchSelectField label="機房" options={rooms} value={roomId} onChange={setRoomId} />
 *   <SearchSelectField label="關聯設備" isMulti value={ids} onChange={setIds}
 *     options={[{ label: "機櫃", options: [...] }, { label: "硬體", options: [...] }]} />
 *
 * 與 Fields.js 同一套慣例:onChange 收到的是「值」不是 event
 * (單選 → 值;多選 → 值陣列;清除 → "" 或 [])。
 * options 可以是平的 {value,label},也可以是分組的 {label, options:[]}。
 */

// 分組/非分組都攤平成一維,方便由值反查選項
const flatten = (options) =>
  options.reduce((acc, o) => acc.concat(o.options ? o.options : [o]), []);

// 清除鍵要有名字也要能用鍵盤。react-select 預設把 × 包在 aria-hidden 的容器裡,
// 而且只掛 onMouseDown —— 讀螢幕的人與鍵盤族都碰不到。換成真的 <button>,
// 行為直接呼叫 react-select 給的 clearValue()。
const ClearIndicator = ({ clearValue, innerProps }) => {
  const { "aria-hidden": _hidden, ...rest } = innerProps || {};
  return (
    <button
      type="button"
      aria-label="清除"
      className="dck-select__clear-indicator btn btn-link p-0 px-2 text-muted"
      {...rest}
      onClick={clearValue}
    >
      <i className="mdi mdi-close" />
    </button>
  );
};
ClearIndicator.propTypes = { clearValue: PropTypes.func, innerProps: PropTypes.object };

// 箭頭跟模板 Choices / form-select 一樣用 MDI chevron,打開時由 SCSS 轉 180°
const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <i className="mdi mdi-chevron-down" aria-hidden="true" />
  </components.DropdownIndicator>
);

// 不要在 render 裡 new 這兩個物件:react-select 當成新 components/styles 會重掛選單,
// 表單每打一字就重掛所有 SearchSelect,整合測試會卡到 timeout。
const SELECT_COMPONENTS = { ClearIndicator, DropdownIndicator };
const SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 1060 }), // 蓋過 modal(1055)
};

export const SearchSelectField = ({
  label, name, value, onChange, required, error, help, disabled,
  options = [], placeholder = "請選擇", isMulti = false, isClearable = true,
  ...rest
}) => {
  const flat = flatten(options);
  const byValue = (v) => flat.find((o) => String(o.value) === String(v)) || null;
  const selected = isMulti
    ? (value || []).map(byValue).filter(Boolean)
    : byValue(value);

  const handle = (picked) => {
    if (!onChange) return;
    onChange(isMulti ? (picked || []).map((o) => o.value) : (picked ? picked.value : ""));
  };

  return (
    <FieldShell label={label} required={required} error={error} help={help}>
      <Select
        inputId={name}
        aria-label={label}
        options={options}
        value={selected}
        onChange={handle}
        isMulti={isMulti}
        isClearable={isClearable}
        isDisabled={disabled}
        placeholder={placeholder}
        noOptionsMessage={() => "查無相符選項"}
        components={SELECT_COMPONENTS}
        // 模板 /form-advanced 的下拉就是用這個前綴;樣式見 plugins/_select2.scss
        className={error ? "select2-selection is-invalid" : "select2-selection"}
        classNamePrefix="select2-selection"
        // 選單掛到 body:放在抽屜/彈窗裡才不會被容器裁掉
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        styles={SELECT_STYLES}
        {...rest}
      />
    </FieldShell>
  );
};
SearchSelectField.propTypes = {
  ...fieldBaseProps,
  options: PropTypes.array,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
};

export default SearchSelectField;
